'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TbChevronLeft, TbChevronRight, TbX } from 'react-icons/tb';
import Logo from '@/app/components/Logo';

const SLIDE_COUNT = 24;
const slides = Array.from({ length: SLIDE_COUNT }, (_, i) => `/assets/company-profile/slide-${String(i).padStart(2, '0')}.webp`);

interface CompanyProfileSlideshowProps {
    locale: 'en' | 'ar';
}

// A faithful rebuild of the standalone company-profile deck (originally a
// static HTML/CSS/JS export) as a proper React piece of this site: same
// splash screen, same full-screen slider with arrows/dots/counter/keyboard/
// swipe — one unified slideshow on every screen size (the original export
// had a different continuous-scroll layout on mobile, but a single
// consistent slideshow reads better here) — using individually optimized +
// lazy-loaded WebP slides, and an explicit exit affordance since this page
// intentionally has no site navbar/footer (see ModernNavbar/Footer/
// GlobalContactButton/CTASection pathname checks for '/company-profile').
//
// The deck itself is a single fixed bilingual asset (every slide already has
// both English and Arabic text baked in), not a per-locale translation, so
// the slide order and slider mechanics stay physically LTR (prev = left,
// next = right, translateX always negative going forward) regardless of
// site locale — only the surrounding UI text/labels are localized.
export default function CompanyProfileSlideshow({ locale }: CompanyProfileSlideshowProps) {
    const isAr = locale === 'ar';
    const [entered, setEntered] = useState(false);
    const [current, setCurrent] = useState(0);
    const touchStartX = useRef<number | null>(null);

    const goTo = useCallback((n: number) => {
        setCurrent(Math.max(0, Math.min(SLIDE_COUNT - 1, n)));
    }, []);

    useEffect(() => {
        if (!entered) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowRight') goTo(current + 1);
            if (e.key === 'ArrowLeft') goTo(current - 1);
            if (e.key === ' ') {
                e.preventDefault();
                goTo(current + 1);
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [entered, current, goTo]);

    const handleTouchStart = (e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
    };
    const handleTouchEnd = (e: React.TouchEvent) => {
        if (touchStartX.current === null) return;
        const dx = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(dx) > 50) goTo(current + (dx > 0 ? 1 : -1));
        touchStartX.current = null;
    };

    return (
        <div className="fixed inset-0 z-[2000] overflow-hidden bg-brand-bg" dir={isAr ? 'rtl' : 'ltr'}>
            {/* Exit affordance — the one piece of "chrome" this immersive page keeps,
                since the global navbar/footer are intentionally hidden here. Fixed
                physical position (not logical start/end) to match the slider below. */}
            <Link
                href={`/${locale}`}
                aria-label={isAr ? 'العودة إلى الموقع' : 'Back to site'}
                className="fixed left-4 top-4 z-[2100] flex h-11 w-11 items-center justify-center rounded-full border border-white/25 bg-black/30 text-white backdrop-blur-md transition-colors duration-200 hover:bg-black/50"
            >
                <TbX className="h-5 w-5" aria-hidden="true" />
            </Link>

            <AnimatePresence>
                {!entered && (
                    <motion.div
                        key="splash"
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.8, ease: 'easeInOut' }}
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-brand-bg"
                    >
                        <motion.div
                            animate={{ scale: [1, 1.07, 1], opacity: [1, 0.85, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                        >
                            <Logo width={160} height={120} className="w-[160px]" />
                        </motion.div>
                        <motion.button
                            type="button"
                            onClick={() => setEntered(true)}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 1.2 }}
                            className="absolute bottom-16 inline-flex items-center gap-2 rounded-full border-2 border-white/60 bg-white/15 px-10 py-3 font-heading text-lg font-semibold tracking-wide text-white backdrop-blur-md transition-all duration-300 hover:scale-105 hover:bg-white/25"
                        >
                            <span>{isAr ? 'ادخل إلى الموقع' : 'Enter the profile'}</span>
                            {isAr ? <TbChevronLeft className="h-5 w-5" aria-hidden="true" /> : <TbChevronRight className="h-5 w-5" aria-hidden="true" />}
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>

            {entered && (
                <div
                    className="h-full w-full"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Forced dir="ltr" — under an inherited dir="rtl" (Arabic locale),
                        `flex-direction: row` lays children out right-to-left instead, which
                        silently breaks the translateX math below (it assumes slide 0 is
                        physically leftmost). Locking this subtree to LTR keeps slide order
                        physical regardless of site locale, matching the deck being a single
                        fixed bilingual asset rather than a per-locale translation. */}
                    <div
                        dir="ltr"
                        className="flex h-full transition-transform duration-[650ms] ease-[cubic-bezier(0.77,0,0.175,1)]"
                        style={{ transform: `translateX(-${current * 100}%)` }}
                    >
                        {slides.map((src, i) => (
                            <div key={src} className="relative flex h-full w-full shrink-0 items-center justify-center p-3">
                                {/* No forced aspect-ratio wrapper — width/height are each slide's own
                                    real dimensions, so the box's shape comes from the image itself
                                    (dynamic) rather than an assumed CSS class, and max-w/max-h just
                                    scale it down to fit the screen while keeping that native shape.
                                    On a portrait phone the 16:9 slide is always width-bound, so it
                                    gets nearly the full width there; desktop caps at 80vw instead. */}
                                <Image
                                    src={src}
                                    alt={`${isAr ? 'شريحة' : 'Slide'} ${i + 1}`}
                                    width={1920}
                                    height={1080}
                                    sizes="(max-width: 767px) 92vw, 80vw"
                                    priority={i === 0}
                                    loading={i === 0 ? undefined : 'lazy'}
                                    className="h-auto max-h-[70vh] w-auto max-w-[92vw] rounded-lg object-contain shadow-[0_20px_60px_rgba(0,0,0,0.35)] md:max-h-[76vh] md:max-w-[80vw]"
                                />
                            </div>
                        ))}
                    </div>

                    <button
                        type="button"
                        onClick={() => goTo(current - 1)}
                        disabled={current === 0}
                        aria-label={isAr ? 'السابق' : 'Previous'}
                        className="fixed left-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-white/30 disabled:pointer-events-none disabled:opacity-25 md:left-[18px] md:h-[54px] md:w-[54px]"
                    >
                        <TbChevronLeft className="h-5 w-5 md:h-6 md:w-6" aria-hidden="true" />
                    </button>
                    <button
                        type="button"
                        onClick={() => goTo(current + 1)}
                        disabled={current === SLIDE_COUNT - 1}
                        aria-label={isAr ? 'التالي' : 'Next'}
                        className="fixed right-2 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-md transition-all duration-200 hover:scale-110 hover:bg-white/30 disabled:pointer-events-none disabled:opacity-25 md:right-[18px] md:h-[54px] md:w-[54px]"
                    >
                        <TbChevronRight className="h-5 w-5 md:h-6 md:w-6" aria-hidden="true" />
                    </button>

                    {/* dir="ltr" so "4 / 24" doesn't get bidi-reordered to "24 / 4" under
                        an inherited dir="rtl". */}
                    <div dir="ltr" className="fixed right-[22px] top-4 z-20 rounded-full bg-black/35 px-3 py-1 text-sm tracking-wide text-white/80">
                        {current + 1} / {SLIDE_COUNT}
                    </div>

                    {/* `inset-x-0` alone (no competing max-width) — that combination was
                        pinning this to the left edge instead of centering it. dir="ltr" so
                        dot order always matches the physically-fixed LTR slide order above,
                        instead of mirroring under Arabic and no longer lining up with which
                        way the (also physically-fixed) arrow buttons move. */}
                    <div dir="ltr" className="fixed inset-x-0 bottom-[18px] z-20 flex flex-wrap items-center justify-center gap-[7px] px-14">
                        {slides.map((_, i) => (
                            <button
                                key={i}
                                type="button"
                                onClick={() => goTo(i)}
                                aria-label={`${isAr ? 'شريحة' : 'Slide'} ${i + 1}`}
                                className={`h-[9px] w-[9px] rounded-full transition-all duration-300 ${
                                    i === current ? 'scale-[1.35] bg-white' : 'bg-white/35'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
